import DepartmentLevelsField from './DepartmentLevelsField';
/**
 * 반 세팅 창(요청서 C4~C12 · B2) — 매니저 전용(오너 2026-10-10 「반 세팅과 관련된 기능은 매니저에게」, 서버도 강사에게 403).
 *   반 만들기 · 이름·부서 수정 · 비활성화 · 다시 활성화 · 담임 교체 · 시간표 변경 · 여러 학생 입반 · 부서 추가.
 * 반에는 개강일 · 종강일이 없다 — 시스템에 만든 날부터 있고, 지우지 않고 비활성화만 한다(오너 결정 2026-10-12, 백엔드 3단계 전달 §1).
 * 담임 · 시간표가 바뀌는 날은 그 값의 첫날이다(기본 오늘, 반을 만든 날보다 앞은 안 됨).
 */
import React, { useState } from 'react';
import { AcademyButton, ActionButton, checkboxClass } from '../controls';
import { useAuth } from '../../../context/AuthContext';
import {
  addClassMembers, changeClassSchedule, changeClassTeacher, createClass, createDepartment, listDepartments, listStudents, setClassActive,
  updateClass,
} from '../academyApi';
import { formatSchedule, isActiveOn, todayKST, weekdayLabel } from '../periodDates';
import { useAsync } from '../ui';
import { DateInput, Field, FormModal, Note, SelectInput, TextInput, TimeInput, homeroomLabel, useHomerooms } from './form';
import { SegTabs } from '../ui';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7].map((d) => [String(d), weekdayLabel(d)]);

/** 시간표 칸 수정 — 요일 · 시작 · 끝, 칸 더하기/빼기 */
function SlotsEditor({ value, onChange }) {
  const set = (i, k, v) => onChange(value.map((s, j) => (j === i ? { ...s, [k]: k === 'weekday' ? Number(v) : v } : s)));
  return (
    <div className="space-y-1.5">
      <div className="space-y-1.5 overflow-x-auto">
        {value.map((s, i) => (
          // eslint-disable-next-line react/no-array-index-key -- 칸은 순서만 있고 고유 번호가 없다
          <div key={i} className="grid min-w-[440px] grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2 rounded-xl bg-gray-50 p-2">
            <div className="flex min-w-0"><SegTabs tabs={WEEKDAYS} value={String(s.weekday)} onChange={(v) => set(i, 'weekday', v)} ariaLabel={`수업 ${i + 1} 요일`} compact /></div>
            <TimeInput aria-label="시작 시각" value={s.start_time} onChange={(v) => set(i, 'start_time', v)} compact />
            <TimeInput aria-label="끝 시각" value={s.end_time} onChange={(v) => set(i, 'end_time', v)} compact />
            <ActionButton iconOnly onClick={() => onChange(value.filter((_, j) => j !== i))} danger>빼기</ActionButton>
          </div>
        ))}
      </div>
      <AcademyButton type="button" onClick={() => onChange([...value, { weekday: 1, start_time: '19:00', end_time: '21:00' }])} variant="secondary">수업 칸</AcademyButton>
    </div>
  );
}
const slotsValid = (slots) => slots.length > 0 && slots.every((s) => s.start_time && s.end_time && s.end_time > s.start_time);
/** 두 시간표가 같은가 — 칸 차례 · 초 단위와 상관없이(서버도 정렬해 비교한다) */
export const sameSlots = (a = [], b = []) => {
  const key = (slots) => JSON.stringify(slots
    .map((s) => [Number(s.weekday), String(s.start_time).slice(0, 5), String(s.end_time).slice(0, 5)])
    .sort((x, y) => x[0] - y[0] || x[1].localeCompare(y[1])));
  return key(a) === key(b);
};
/** 그날 걸린 기간(담임 · 시간표 판) — [start_on, end_on) */
const periodOn = (rows = [], d) => rows.find((r) => isActiveOn(r, d)) || null;

function useDepartments() {
  const { getAccessToken } = useAuth();
  const r = useAsync((signal) => listDepartments({ signal }, getAccessToken), 'depts-class');
  return r.data || [];
}

/**
 * 반 수정 창의 부서 고르기 — 감추지 않은 부서 + 그 반의 지금 부서(감춘 부서면 「이름 (감춤)」).
 * 목록에 없으면 고르기가 「부서 없음」으로 보이는데 저장하면 감춘 부서 번호가 그대로 나가 다시 활성화가 계속 막혔다
 * (백엔드 4단계 전달 §4-G — 3단계 점검).
 */
export function classDeptOptions(depts, current) {
  const options = depts.map((d) => [d.department_id, d.name]);
  if (current && !depts.some((d) => d.department_id === current.department_id)) {
    options.push([current.department_id, depts.length ? `${current.name} (감춤)` : current.name]);
  }
  return options;
}

/** C4 반 만들기 · C5 이름·부서 수정 — 반은 만든 날(오늘)부터. 개강일 · 정정 사유 칸은 없다(백엔드 3단계 전달 §4-B) */
export function ClassFormModal({ cls, onClose, onCreated }) {
  const { getAccessToken } = useAuth();
  const homerooms = useHomerooms();
  const depts = useDepartments();
  const editing = !!cls;
  const [name, setName] = useState(cls?.name || '');
  const [deptId, setDeptId] = useState(cls?.department?.department_id ? String(cls.department.department_id) : '');
  const [teacherId, setTeacherId] = useState('');
  const [slots, setSlots] = useState([{ weekday: 2, start_time: '19:00', end_time: '21:00' }, { weekday: 4, start_time: '19:00', end_time: '21:00' }]);
  return (
    <FormModal
      onClose={onClose}
      title={editing ? `${cls.name} — 반 정보 수정` : '반 만들기'}
      size="lg"
      submitLabel={editing ? '저장' : '반 만들기'}
      canSubmit={!!name.trim() && (editing || (!!teacherId && slotsValid(slots)))}
      onSubmit={async () => {
        if (editing) {
          await updateClass(cls.class_id, { name: name.trim(), departmentId: deptId ? Number(deptId) : null, ifRevision: cls.revision }, getAccessToken);
          return;
        }
        const d = await createClass({ name: name.trim(), departmentId: deptId ? Number(deptId) : null, teacherStaffId: Number(teacherId), slots }, getAccessToken);
        if (d?.class_id && onCreated) onCreated(d.class_id);
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="반 이름" required><TextInput value={name} onChange={setName} /></Field>
        <Field label="부서"><SelectInput value={deptId} onChange={setDeptId} options={classDeptOptions(depts, editing ? cls.department : null)} placeholder="부서 없음" /></Field>
        {!editing && (
          <Field label="첫 담임" required>
            <SelectInput value={teacherId} onChange={setTeacherId} options={homerooms.map((t) => [t.staff_id, homeroomLabel(t)])} placeholder="담임 선택" />
          </Field>
        )}
      </div>
      {!editing && (
        <Field label="수업 시간표" required>
          <SlotsEditor value={slots} onChange={setSlots} />
        </Field>
      )}
    </FormModal>
  );
}

/**
 * 비활성화 · 다시 활성화 — 확인만 한다(날짜 · 사유 칸 없음, 백엔드 3단계 전달 §4-A). 반은 지우지 않는다.
 * 비활성화할 때 학생 편성을 어떻게 할지는 4단계에서 정한다 — 여기서는 편성을 말하지 않는다.
 */
export function ClassActiveModal({ cls, onClose }) {
  const { getAccessToken } = useAuth();
  const deactivate = cls.active;
  // 다시 활성화 — 반의 부서가 감춰져 있으면 서버가 409 로 막는다. 「부서를 비우고 다시 활성화」를 고르면 같은 요청에 부서 없음을 싣는다
  const depts = useAsync((signal) => (!deactivate && cls.department
    ? listDepartments({ includeArchived: true, signal }, getAccessToken) : Promise.resolve([])), `depts-active-${cls.class_id}`);
  const hiddenDept = (depts.data || []).find((d) => d.department_id === cls.department?.department_id && d.archived) || null;
  const [clearDept, setClearDept] = useState(false);
  return (
    <FormModal
      onClose={onClose}
      title={`${cls.name} — ${deactivate ? '비활성화' : '다시 활성화'}`}
      danger={deactivate}
      submitLabel={deactivate ? '비활성화' : '다시 활성화'}
      canSubmit={deactivate || !hiddenDept || clearDept}
      onSubmit={() => setClassActive(cls.class_id, { active: !deactivate, ...(hiddenDept && clearDept ? { departmentId: null } : {}), ifRevision: cls.revision }, getAccessToken)}
    >
      {deactivate ? (
        <Note tone="warn">
          학생이 남아 있으면 비활성화할 수 없습니다.
        </Note>
      ) : null}
      {hiddenDept && (
        <>
          <Note tone="warn">이 반의 부서 「{hiddenDept.name}」가 감춰져 있어 그대로는 다시 활성화할 수 없습니다.</Note>
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input type="checkbox" className={checkboxClass} checked={clearDept} onChange={(e) => setClearDept(e.target.checked)} />
            부서를 비우고 다시 활성화
          </label>
        </>
      )}
    </FormModal>
  );
}

/** 바뀌는 날 칸의 안내(백엔드 3단계 전달 §3 · §4-D) */
const CHANGE_DAY_HINT = '같은 날의 기존 변경은 덮어씁니다.';

/** C8 담임 교체 — 바뀌는 날부터 새 담임. 그날 담임과 같은 사람은 고를 수 없다 */
export function TeacherChangeModal({ cls, onClose }) {
  const { getAccessToken } = useAuth();
  const homerooms = useHomerooms();
  const [staffId, setStaffId] = useState('');
  const [startOn, setStartOn] = useState(todayKST());
  const onDay = periodOn(cls.teachers, startOn);
  const tooEarly = !!cls.created_on && !!startOn && startOn < cls.created_on;
  return (
    <FormModal onClose={onClose} title={`${cls.name} — 담임 교체`} submitLabel="담임 교체 저장" canSubmit={!!staffId && !!startOn && !tooEarly}
      onSubmit={() => changeClassTeacher(cls.class_id, { staffId: Number(staffId), startOn, ifRevision: cls.revision }, getAccessToken)}>
      <Note>현재 담임 {onDay?.name || '없음'}</Note>
      <Field label="바뀌는 날 — 새 담임의 첫날" required hint={CHANGE_DAY_HINT}>
        <DateInput value={startOn} onChange={(v) => { setStartOn(v); setStaffId(''); }} min={cls.created_on || undefined} />
      </Field>
      {tooEarly && <Note tone="warn">반을 만든 날({cls.created_on})보다 앞 날짜로는 바꿀 수 없습니다.</Note>}
      <Field label="새 담임" required>
        <SelectInput value={staffId} onChange={setStaffId} options={homerooms.filter((t) => t.staff_id !== onDay?.staff_id).map((t) => [t.staff_id, homeroomLabel(t)])} placeholder="새 담임 선택" />
      </Field>
    </FormModal>
  );
}

/** C10 시간표 변경 — 바뀌는 날부터 새 판. 그날 시간표와 같으면 저장할 수 없다. 과거 회차 · 시수 계산은 바뀌지 않는다 */
export function ScheduleChangeModal({ cls, onClose }) {
  const { getAccessToken } = useAuth();
  const [startOn, setStartOn] = useState(todayKST());
  const [slots, setSlots] = useState(cls.slots.map((s) => ({ ...s })));
  const onDay = periodOn(cls.schedules, startOn);
  const same = !!onDay && sameSlots(onDay.slots, slots);
  const tooEarly = !!cls.created_on && !!startOn && startOn < cls.created_on;
  return (
    <FormModal onClose={onClose} title={`${cls.name} — 시간표 변경`} size="lg" submitLabel="시간표 저장" canSubmit={!!startOn && !tooEarly && slotsValid(slots) && !same}
      onSubmit={() => changeClassSchedule(cls.class_id, { startOn, slots, ifRevision: cls.revision }, getAccessToken)}>
      <Note>현재 시간표 {formatSchedule(onDay?.slots) || '없음'}</Note>
      <Field label="바뀌는 날 — 새 시간표의 첫날" required hint={CHANGE_DAY_HINT}>
        <DateInput value={startOn} onChange={setStartOn} min={cls.created_on || undefined} />
      </Field>
      {tooEarly && <Note tone="warn">반을 만든 날({cls.created_on})보다 앞 날짜로는 바꿀 수 없습니다.</Note>}
      <Field label="수업 칸" required><SlotsEditor value={slots} onChange={setSlots} /></Field>
      {same && <Note tone="warn">그날 시간표와 같습니다 — 바꿀 칸을 수정해 주세요.</Note>}
    </FormModal>
  );
}

/** C12 여러 학생 입반 — 전부 성공 또는 전부 취소, 실패하면 학생별 사유 */
export function AddMembersModal({ cls, memberIds = [], onClose }) {
  const { getAccessToken } = useAuth();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState([]);
  // 고른 학생의 이름 — 검색어를 바꿔 목록에서 사라져도 학생별 실패 사유에 이름이 서게 기억한다
  const [names, setNames] = useState({});
  const [startOn, setStartOn] = useState(todayKST());
  const found = useAsync((signal) => listStudents({ q: q.trim() || undefined, state: ['enrolled', 'paused', 'register_scheduled'], perPage: 30, signal }, getAccessToken), `members-search-${q}`);
  const toggle = (s, on) => {
    setPicked(on ? [...picked, s.student_id] : picked.filter((x) => x !== s.student_id));
    if (on) setNames((prev) => ({ ...prev, [s.student_id]: s.student_name }));
  };
  return (
    <FormModal onClose={onClose} title={`${cls.name} — 학생 입반`} size="lg" submitLabel={`${picked.length}명 입반`} canSubmit={picked.length > 0 && !!startOn} names={names}
      onSubmit={() => addClassMembers(cls.class_id, { studentIds: picked, startOn, ifRevision: cls.revision }, getAccessToken)}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="학생 찾기"><TextInput value={q} onChange={setQ} placeholder="이름 · 학교" /></Field>
        <Field label="입반일" required hint="반을 만든 날 이후 · 학생마다 재원 기간 안"><DateInput value={startOn} onChange={setStartOn} min={cls.created_on || undefined} /></Field>
      </div>
      <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-xl border border-gray-200 px-2">
        {(found.data?.items || []).map((s) => {
          const already = memberIds.includes(s.student_id);
          return (
            <label key={s.student_id} className={`flex items-center gap-2 py-1.5 text-xs ${already ? 'text-gray-400' : 'text-gray-800'}`}>
              <input type="checkbox" className={checkboxClass} disabled={already} checked={picked.includes(s.student_id)} onChange={(e) => toggle(s, e.target.checked)} />
              <span className="w-8 text-gray-500" title={s.student_no ? `출결번호 ${s.student_no}` : '출결번호 없음'}>{s.student_no || ''}</span>
              <span className="font-medium">{s.student_name}</span>
              <span className="text-gray-500">{s.school?.name || '학교 미지정'} {s.grade_label}</span>
              <span className="ml-auto truncate text-gray-400">{already ? '이미 이 반' : s.classes.map((c) => c.class_name).join(' · ') || '반 없음'}</span>
            </label>
          );
        })}
      </div>
    </FormModal>
  );
}

/** B2 부서 추가 — 부서 자동 선택 기준은 새 학생 창이 학년으로 부서를 미리 고를 때 쓴다(저장은 막지 않음) */
export function DepartmentAddModal({ onClose }) {
  const { getAccessToken } = useAuth();
  const [name, setName] = useState('');
  const [levels, setLevels] = useState([]);
  return (
    <FormModal onClose={onClose} title="부서 추가" submitLabel="부서 추가" canSubmit={!!name.trim()}
      onSubmit={() => createDepartment({ name: name.trim(), schoolLevels: levels }, getAccessToken)}>
      <Field label="부서 이름" required><TextInput value={name} onChange={setName} placeholder="예: 특목고반" /></Field>
      <DepartmentLevelsField value={levels} onChange={setLevels} />
    </FormModal>
  );
}
